import { useEffect, useRef } from 'preact/hooks';
import useFormStore from '../store/useFormStore';
import {
  trackFormStart,
  trackSelectService,
  trackServiceDetails,
  trackScheduleServices,
  trackYourInformation,
  trackScheduleAppointment,
  hasPromoApplied
} from '../util/dataLayerTracking';

/**
 * DataLayerTracker Component
 *
 * Automatically tracks GTM events based on currentScreen changes.
 * Embed once in App.js - no manual tracking calls needed.
 */
export default function DataLayerTracker() {
  const currentScreen = useFormStore((state) => state.currentScreen);
  const currentPostalCode = useFormStore((state) => state.currentPostalCode);
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const selectedServices = useFormStore((state) => state.selectedServices);
  const selectedServicesConfiguration = useFormStore((state) => state.selectedServicesConfiguration);
  const scheduledAppointments = useFormStore((state) => state.scheduledAppointments);
  const userFormInformation = useFormStore((state) => state.userFormInformation);
  const cart = useFormStore((state) => state.cart);
  const appliedPromotions = useFormStore((state) => state.appliedPromotions);
  const manuallyEnteredPromoCodes = useFormStore((state) => state.manuallyEnteredPromoCodes);
  const getServiceById = useFormStore((state) => state.getServiceById);

  useEffect(() => {

    switch (currentScreen) {
      case 'select-service':
        // Track form start when franchise is loaded
        if (currentFranchise && currentPostalCode) {
          trackFormStart(
            currentPostalCode,
            true,
            currentFranchise
          );
        }
        break;

      case 'configure-services':
        if (currentFranchise?.services && selectedServices.length > 0) {
          // Extract available service names from franchise services
          const availableServices = currentFranchise.services.map(s => s.name);

          // Extract selected service names by looking up in currentFranchise.services
          const selectedServiceNames = selectedServices.map(service => {
            const serviceId = service.id || service;
            return getServiceById(serviceId)?.name || service.name || service;
          });

          trackSelectService(availableServices, selectedServiceNames);
        }
        break;

      case 'schedule-service':
        // Track service details configuration
        if (selectedServices.length > 0 && cart?.total !== undefined && currentFranchise?.services) {
          // Build services array with details
          const servicesWithDetails = selectedServices.map(service => {
            const serviceId = service.id || service;
            const serviceName = getServiceById(serviceId)?.name || '';
            const config = selectedServicesConfiguration[serviceId] || {};

            // Extract detail columns that have any quantity > 0
            // config structure: { "row_id": { clean: 2, deodorize: 1, protect: 0 }, ... }
            const detailsSet = new Set();

            Object.values(config).forEach(rowConfig => {
              if (typeof rowConfig === 'object') {
                Object.entries(rowConfig).forEach(([column, quantity]) => {
                  if (quantity > 0) {
                    detailsSet.add(column);
                  }
                });
              }
            });

            return {
              service_type: serviceName,
              details: Array.from(detailsSet)
            };
          });

          trackServiceDetails(servicesWithDetails, cart.total);
        }
        break;

      case 'information':
        // Track appointment scheduling
        if (scheduledAppointments.length > 0 && cart?.total !== undefined) {
          // Build appointments array with services for each appointment
          const appointments = scheduledAppointments.map(appt => {
            // Get service names for this appointment
            const serviceNames = Object.values(appt.services || {});

            return {
              date: appt.timeslot?.date || '',
              arrival_window: appt.timeslot?.timeslot || '',
              services: serviceNames
            };
          });

          const promoApplied = hasPromoApplied(appliedPromotions, manuallyEnteredPromoCodes);

          trackScheduleServices(
            appointments,
            cart.total,
            promoApplied
          );
        }
        break;

      case 'review':
        // Track user information submission
        if (userFormInformation) {
          const emailOptIn = userFormInformation.signUpForEmails || false;
          const smsOptIn = userFormInformation.signUpForTexts || false;

          trackYourInformation(emailOptIn, smsOptIn);
        }
        break;

      case 'confirmation':
        // Track final appointment confirmation
        if (cart?.total !== undefined) {
          const promoApplied = hasPromoApplied(appliedPromotions, manuallyEnteredPromoCodes);

          trackScheduleAppointment(cart.total, promoApplied);
        }
        break;

      default:
        // No tracking for other screens
        break;
    }
  }, [currentScreen]);

  return null;
}
